// src/pages/Clientes.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  Timestamp,
  query,
  limit,
  orderBy,
  where
} from 'firebase/firestore';
import {
  Plus, Trash2, Car, Pencil,
  History, X, Search, Phone, FileText, CalendarDays, User as UserIcon
} from 'lucide-react';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

import { useEmpresaId } from "../context/EmpresaContext";
import ModalNovoCliente from "../components/ModalNovoCliente";

const db = getFirestore();

const defaultForm = {
  nome: '', telefone: '', email: '', documento: '', cpf: '', cnpj: '',
  cep: '', endereco: '', numero: '', complemento: '', bairro: '',
  cidade: '', estado: '', obs: '', codigoIBGE: ''
};

/* ===== Botões tom-sobre-tom ===== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'secondary':
      return 'text-indigo-800 bg-indigo-100 hover:bg-indigo-200 dark:text-indigo-300 dark:bg-indigo-900/50 dark:hover:bg-indigo-900';
    case 'neutral':
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
    case 'danger':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};
const ActionBtn = ({ tone='neutral', icon: Icon, className='', disabled, children, ...props }) => {
  const base = `inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md ${toneStyles(tone)} ${
    disabled ? 'opacity-60 cursor-not-allowed' : ''
  } ${className}`;
  return (
    <button className={base} disabled={disabled} {...props}>
      {Icon && <Icon size={16} className="text-current" />} {children}
    </button>
  );
};
const ActionIcon = ({ tone='neutral', title, className='', children, ...props }) => (
  <button
    title={title}
    aria-label={title}
    className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold transition ${toneStyles(tone)} ${className}`}
    {...props}
  >
    {children}
  </button>
);

/* ===== Modal de histórico ===== */
const HistoryModal = ({ isOpen, onClose, history, clientName, loading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl">
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Histórico de Atendimentos: {clientName}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 dark:hover:text-white p-2 rounded-full">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <p className="text-center text-gray-500 py-8">A carregar histórico...</p>
          ) : history.length === 0 ? (
            <div className="text-center py-10">
              <History size={40} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Nenhum histórico encontrado para este cliente.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map(os => (
                <div key={os.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg flex justify-between items-center">
                  <div>
                    <p className="font-bold">OS #{os.numeroOS || os.id.slice(-4)}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{os.veiculoDescricao}</p>
                    <p className="text-xs text-gray-500">
                      Data: {os.dataAbertura ? os.dataAbertura.toDate().toLocaleDateString('pt-BR') : 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold px-2 py-1 rounded-full inline-block ${
                      os.status === 'Finalizada' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {os.status}
                    </p>
                    <p className="font-bold text-lg mt-1">R$ {(os.valorTotalGeral || 0).toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ===== Página ===== */
const Clientes = () => {
  const empresaId = useEmpresaId();
  const [clientes, setClientes] = useState([]);
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'table'

  // Ordenação via cabeçalho
  const [sortField, setSortField] = useState('nome'); // 'nome' | 'criadoEm'
  const [sortDir, setSortDir] = useState('asc');      // 'asc' | 'desc'

  const [form, setForm] = useState(defaultForm);
  const [editandoId, setEditandoId] = useState(null);
  const navigate = useNavigate();

  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clienteHistory, setClienteHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (empresaId) buscarClientes();
  }, [empresaId]);

  const colClientes = (id) => collection(db, 'empresas', id, 'clientes');
  const colOS = (id) => collection(db, 'empresas', id, 'os');

  const buscarClientes = async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      // busca server-side por nome para reduzir transferência;
      // ordenação final é aplicada client-side.
      const q = query(colClientes(empresaId), orderBy('nome'), limit(200));
      const snapshot = await getDocs(q);
      setClientes(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Erro ao buscar clientes: ", error);
    }
    setLoading(false);
  };

  const handleNovoCliente = () => {
    setForm(defaultForm);
    setEditandoId(null);
    setIsModalOpen(true);
  };

  const handleEditarCliente = (cliente) => {
    setForm(cliente);
    setEditandoId(cliente.id);
    setIsModalOpen(true);
  };

  const handleSalvar = async ({ formData, veiculos }) => {
    if (!empresaId) return;
    if (!formData.nome || !formData.telefone) {
      Swal.fire('Atenção', 'Nome e Telefone são obrigatórios.', 'warning');
      return;
    }

    const payload = {
      ...formData,
      nome_lowercase: formData.nome.toLowerCase(),
      criadoEm: editandoId ? formData.criadoEm : Timestamp.now(),
      atualizadoEm: Timestamp.now()
    };

    try {
      let clienteId = editandoId;

      if (editandoId) {
        await updateDoc(doc(db, 'empresas', empresaId, 'clientes', editandoId), payload);
      } else {
        const clienteDocRef = await addDoc(colClientes(empresaId), payload);
        clienteId = clienteDocRef.id;
      }

      if (clienteId && veiculos && veiculos.length > 0) {
        const promessasVeiculos = veiculos.map((veiculo) => {
          const veiculoPayload = { ...veiculo, clienteId };
          return addDoc(collection(db, 'empresas', empresaId, 'clientes', clienteId, 'veiculos'), veiculoPayload);
        });
        await Promise.all(promessasVeiculos);
      }

      Swal.fire('Sucesso!', `Cliente ${editandoId ? 'atualizado' : 'cadastrado'} com sucesso.`, 'success');
      setIsModalOpen(false);
      buscarClientes();
    } catch (error) {
      console.error("Erro ao salvar cliente e veículos:", error);
      Swal.fire('Erro', 'Não foi possível salvar os dados.', 'error');
    }
  };

  const handleExcluir = async (id) => {
    if (!empresaId) return;
    const result = await Swal.fire({
      title: 'Deseja excluir este cliente?',
      text: 'Esta ação não poderá ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, 'empresas', empresaId, 'clientes', id));
      buscarClientes();
      Swal.fire('Excluído!', 'O cliente foi removido.', 'success');
    }
  };

  const handleShowHistory = async (cliente) => {
    if (!empresaId) return;
    setSelectedClient(cliente);
    setIsHistoryModalOpen(true);
    setLoadingHistory(true);
    try {
      const q = query(colOS(empresaId), where("clienteId", "==", cliente.id), orderBy("dataAbertura", "desc"));
      const snapshot = await getDocs(q);
      setClienteHistory(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Erro ao buscar histórico do cliente:", error);
    }
    setLoadingHistory(false);
  };

  // Filtro de busca
  const clientesFiltrados = useMemo(() => {
    if (!busca) return clientes;
    const buscaLower = busca.toLowerCase();
    return clientes.filter((c) =>
      (c.nome || '').toLowerCase().includes(buscaLower) ||
      (c.telefone || '').includes(busca) ||
      ((c.documento || c.cpf || c.cnpj || '') || '').includes(busca)
    );
  }, [clientes, busca]);

  // Ordenação (aplicada sobre o resultado filtrado)
  const clientesOrdenados = useMemo(() => {
    const arr = [...clientesFiltrados];
    const dir = sortDir === 'asc' ? 1 : -1;

    arr.sort((a, b) => {
      if (sortField === 'nome') {
        const an = (a.nome || '').toLocaleLowerCase();
        const bn = (b.nome || '').toLocaleLowerCase();
        return an.localeCompare(bn, 'pt-BR') * dir;
      } else {
        const at = a.criadoEm?.toDate ? a.criadoEm.toDate().getTime() : 0;
        const bt = b.criadoEm?.toDate ? b.criadoEm.toDate().getTime() : 0;
        return (at - bt) * dir;
      }
    });

    return arr;
  }, [clientesFiltrados, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Indicador de seta SEMPRE visível
  const SortIndicator = ({ field }) => {
    const isActive = sortField === field;
    return (
      <span
        className={`ml-1 text-[10px] leading-none ${
          isActive ? 'text-black dark:text-white' : 'text-gray-400 dark:text-gray-500'
        }`}
      >
        {isActive ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
      </span>
    );
  };

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Clientes</h1>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-grow min-w-[220px]">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome, telefone..."
              className="border px-3 py-2 pl-10 rounded-lg w-full bg-white dark:bg-gray-800 dark:border-gray-700"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>

          {/* Toggle de visualização */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <button
              className={`px-3 py-2 text-sm font-semibold ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
              onClick={() => setViewMode('cards')}
              type="button"
            >
              Cards
            </button>
            <button
              className={`px-3 py-2 text-sm font-semibold ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}
              onClick={() => setViewMode('table')}
              type="button"
            >
              Tabela
            </button>
          </div>

          <button
            onClick={handleNovoCliente}
            className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            disabled={!empresaId}
          >
            <Plus size={18} /> Novo Cliente
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-10">A carregar clientes...</p>
      ) : clientesOrdenados.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl">
          <UserIcon size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200">Nenhum cliente encontrado</h3>
          <p className="text-gray-500 mt-2">Que tal adicionar o primeiro?</p>
        </div>
      ) : viewMode === 'cards' ? (
        /* ====== GRID DE CARDS ====== */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {clientesOrdenados.map((c) => (
            <div
              key={c.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-md flex flex-col transition hover:shadow-xl hover:-translate-y-1"
            >
              {/* Cabeçalho */}
              <div className="p-4 flex items-start gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-md">
                  <UserIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{c.nome}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{c.email || 'E-mail não informado'}</p>
                </div>
              </div>

              {/* Corpo */}
              <div className="p-5 space-y-3 text-sm flex-grow border-t border-b dark:border-gray-700">
                <InfoItem icone={Phone} texto={c.telefone || 'Não informado'} />
                <InfoItem icone={FileText} texto={c.documento || c.cpf || c.cnpj || 'Documento não informado'} />
                <InfoItem
                  icone={CalendarDays}
                  texto={`Cliente desde: ${
                    c.criadoEm && c.criadoEm.toDate ? c.criadoEm.toDate().toLocaleDateString('pt-BR') : 'N/A'
                  }`}
                />
              </div>

              {/* Rodapé com ações */}
              <div className="p-3 flex flex-wrap items-center justify-between gap-2 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
                <div className="flex gap-2">
                  <ActionBtn tone="primary" icon={Car} onClick={() => navigate(`/clientes/${c.id}/veiculos`)}>
                    Veículos
                  </ActionBtn>
                  <ActionBtn tone="secondary" icon={History} onClick={() => handleShowHistory(c)}>
                    Histórico
                  </ActionBtn>
                </div>
                <div className="flex gap-2">
                  <ActionBtn tone="neutral" icon={Pencil} onClick={() => handleEditarCliente(c)}>
                    Editar
                  </ActionBtn>
                  <ActionBtn tone="danger" icon={Trash2} onClick={() => handleExcluir(c.id)}>
                    Excluir
                  </ActionBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ====== TABELA ====== */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="sticky top-0 z-10 text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleSort('nome')}
                    aria-sort={sortField === 'nome' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className="font-semibold inline-flex items-center text-gray-900 dark:text-gray-100 cursor-pointer"
                  >
                    Nome <SortIndicator field="nome" />
                  </button>
                </th>
                <th className="px-6 py-3">Telefone</th>
                <th className="px-6 py-3">E-mail</th>
                <th className="px-6 py-3">Documento</th>
                <th className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => toggleSort('criadoEm')}
                    aria-sort={sortField === 'criadoEm' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className="font-semibold inline-flex items-center text-gray-900 dark:text-gray-100 cursor-pointer"
                  >
                    Desde <SortIndicator field="criadoEm" />
                  </button>
                </th>
                <th className="px-6 py-3 text-center w-[220px]">Ações</th>
              </tr>
            </thead>
            <tbody>
              {clientesOrdenados.map((c) => (
                <tr key={c.id} className="border-b dark:border-gray-700 hover:bg-gray-50/60 dark:hover:bg-gray-700/30">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-800 dark:text-gray-100">
                    {c.nome}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{c.telefone || '-'}</td>
                  <td className="px-6 py-4 truncate max-w-[280px]">
                    {c.email || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {c.documento || c.cpf || c.cnpj || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {c.criadoEm && c.criadoEm.toDate ? c.criadoEm.toDate().toLocaleDateString('pt-BR') : 'N/A'}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2 justify-center">
                      <ActionIcon tone="primary" title="Veículos" onClick={() => navigate(`/clientes/${c.id}/veiculos`)}>
                        <Car size={16} />
                      </ActionIcon>
                      <ActionIcon tone="secondary" title="Histórico" onClick={() => handleShowHistory(c)}>
                        <History size={16} />
                      </ActionIcon>
                      <ActionIcon tone="neutral" title="Editar" onClick={() => handleEditarCliente(c)}>
                        <Pencil size={16} />
                      </ActionIcon>
                      <ActionIcon tone="danger" title="Excluir" onClick={() => handleExcluir(c.id)}>
                        <Trash2 size={16} />
                      </ActionIcon>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ModalNovoCliente
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSalvar}
        initialData={form}
        editando={!!editandoId}
      />

      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        history={clienteHistory}
        clientName={selectedClient?.nome}
        loading={loadingHistory}
      />
    </div>
  );
};

const InfoItem = ({ icone: Icone, texto }) => (
  <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
    <Icone size={16} className="text-gray-400" />
    <span>{texto}</span>
  </div>
);

export default Clientes;
